import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { OPTOMETRIST_QUESTIONS, DISPENSER_QUESTIONS } from '../data/questions';
import ScorecardForm from '../components/ScorecardForm';

const API =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  'http://127.0.0.1:5000';

const tok = () => localStorage.getItem('portalToken');

// ── model answer renderer ─────────────────────────────────────────────────────

function ModelAnswer({ ma }) {
  if (!ma) return null;
  return (
    <div style={{ fontSize: 13, lineHeight: 1.7 }}>
      {ma.note && (
        <div style={{ color: 'var(--text2)', fontStyle: 'italic', marginBottom: 8 }}>
          {ma.note}
        </div>
      )}
      {ma.points && ma.points.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          <span style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }}>•</span>
          <span>{p}</span>
        </div>
      ))}
      {ma.table && (
        <table style={{ borderCollapse: 'collapse', marginTop: 8, marginBottom: 8, width: '100%' }}>
          <thead>
            <tr>
              {ma.table.headers.map((h, i) => (
                <th key={i} style={{ padding: '5px 10px', background: 'var(--bg4)', border: '1px solid var(--border2)', fontSize: 12, fontWeight: 600, textAlign: 'left', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ma.table.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} style={{ padding: '5px 10px', border: '1px solid var(--border)', fontSize: 13, fontFamily: 'monospace', color: 'var(--text)' }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {ma.summary && (
        <div style={{ marginTop: 8, padding: '6px 12px', background: 'var(--bg4)', borderLeft: '3px solid var(--accent)', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0', fontWeight: 500, color: 'var(--accent2)', fontSize: 13 }}>
          {ma.summary}
        </div>
      )}
    </div>
  );
}

// ── category chip ─────────────────────────────────────────────────────────────

const CATEGORY_COLOURS = {
  'Binocular balancing': { bg: 'var(--blue-bg)',   color: 'var(--blue)'   },
  'Reading add':         { bg: 'var(--purple-bg)', color: 'var(--purple)' },
  'High prescription':   { bg: 'var(--amber-bg)',  color: 'var(--amber)'  },
  'Optical centre':      { bg: 'var(--coral-bg)',  color: 'var(--coral)'  },
  'Pathology':           { bg: 'var(--red-bg)',    color: 'var(--red)'    },
  'Paediatric':          { bg: 'var(--green-bg)',  color: 'var(--green)'  },
};

function CategoryBadge({ label, size = 'normal' }) {
  const c = CATEGORY_COLOURS[label] || { bg: 'var(--bg4)', color: 'var(--text2)' };
  return (
    <span style={{
      display: 'inline-block',
      padding: size === 'sm' ? '2px 8px' : '3px 10px',
      borderRadius: 20,
      fontSize: size === 'sm' ? 11 : 12,
      fontWeight: 500,
      background: c.bg,
      color: c.color,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function AdminMarkTest() {
  const { id } = useParams();

  const [applicant,  setApplicant]  = useState(null);
  const [testData,   setTestData]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  const [marks,      setMarks]      = useState({});   // { answer_id: { is_correct, mark_reason } }
  const [marking,    setMarking]    = useState(false);
  const [markMsg,    setMarkMsg]    = useState('');

  const [tab,        setTab]        = useState('answers');
  const [catFilter,  setCatFilter]  = useState('All');

  const user    = JSON.parse(localStorage.getItem('portalUser') || 'null');
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [aRes, tRes] = await Promise.all([
          fetch(`${API}/api/admin/applicants/${id}`,      { headers: { Authorization: `Bearer ${tok()}` } }),
          fetch(`${API}/api/admin/applicants/${id}/test`, { headers: { Authorization: `Bearer ${tok()}` } }),
        ]);
        const aData = await aRes.json();
        const tData = await tRes.json();
        if (!aRes.ok) throw new Error(aData.error || 'Could not load applicant');
        setApplicant(aData.applicant || aData);
        if (tRes.ok && tData.test) {
          setTestData(tData.test);
          const m = {};
          (tData.test.answers || []).forEach(a => {
            m[a.id] = { is_correct: a.is_correct, mark_reason: a.mark_reason || '' };
          });
          setMarks(m);
        }
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const handleMarkSave = async () => {
    try {
      setMarking(true); setMarkMsg('');
      const payload = Object.entries(marks).map(([aid, m]) => ({
        answer_id: Number(aid),
        is_correct: m.is_correct,
        mark_reason: m.mark_reason || '',
      }));
      const res = await fetch(`${API}/api/admin/applicants/${id}/test/mark`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ marks: payload }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not save');
      setTestData(d.test);
      setMarkMsg('Marks saved.');
    } catch (e) { setMarkMsg(e.message || 'Error saving marks'); }
    finally { setMarking(false); }
  };

  // ── derived values ───────────────────────────────────────────────────────────

  if (!user) return <p style={{ padding: 24, color: 'var(--text2)' }}>Please log in.</p>;
  if (loading) return <p style={{ padding: 24, color: 'var(--text2)' }}>Loading…</p>;
  if (error)   return <p style={{ padding: 24, color: 'var(--red)' }}>{error}</p>;

  const questions = testData
    ? (testData.role === 'optometrist' ? OPTOMETRIST_QUESTIONS : DISPENSER_QUESTIONS)
    : [];

  const roleLabel = testData?.role === 'optometrist' ? 'Optometrist' : 'Optical Dispenser';
  const candidateName = applicant
    ? `${applicant.user?.first_name || ''} ${applicant.user?.last_name || ''}`.trim()
    : '';
  const testDate = testData?.submitted_at
    ? new Date(testData.submitted_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  // category chips
  const categories = ['All', ...Array.from(new Set(questions.map(q => q.category).filter(Boolean)))];

  // scoring summary
  const answers = testData?.answers || [];
  const correctCount   = answers.filter(a => marks[a.id]?.is_correct === true).length;
  const incorrectCount = answers.filter(a => marks[a.id]?.is_correct === false).length;
  const unmarkedCount  = answers.filter(a => marks[a.id]?.is_correct == null).length;
  const score          = correctCount; // out of 10
  const remaining      = unmarkedCount;

  // filtered questions for display
  const visibleQuestions = catFilter === 'All'
    ? questions
    : questions.filter(q => q.category === catFilter);

  const tabBtn = (key, label) => (
    <button type="button" onClick={() => setTab(key)} style={{
      padding: '8px 20px', border: 'none',
      borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
      background: 'transparent',
      color: tab === key ? 'var(--accent)' : 'var(--text2)',
      fontWeight: tab === key ? 600 : 400,
      cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 14,
    }}>{label}</button>
  );

  return (
    <div>
      {/* ── breadcrumb ── */}
      <div style={{ marginBottom: 20, fontSize: 14, color: 'var(--text2)' }}>
        <Link to="/">Talent Pool</Link>
        <span style={{ margin: '0 8px' }}>›</span>
        <Link to={`/candidate/${id}`}>{candidateName || 'Candidate'}</Link>
        <span style={{ margin: '0 8px' }}>›</span>
        <span style={{ color: 'var(--text)' }}>Mark Assessment</span>
      </div>

      {/* ── page header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <h1 style={{ margin: 0 }}>{roleLabel} candidate test</h1>
          <span className="badge badge-gold">{questions.length} questions</span>
        </div>
        <p style={{ color: 'var(--text2)', fontSize: 14, margin: 0 }}>
          Admin marking tool — mark each answer correct or incorrect. Score out of 10 is calculated automatically.
        </p>
      </div>

      {/* ── tabs ── */}
      <div style={{ borderBottom: '1px solid var(--border)', marginBottom: 24, display: 'flex' }}>
        {tabBtn('answers', 'Answers & Marking')}
        {tabBtn('scorecard', 'Scorecard')}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'answers' && (
        <div>
          {!testData && <p style={{ color: 'var(--text3)' }}>No test submitted yet for this candidate.</p>}

          {testData && (
            <>
              {/* ── category chips ── */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                {categories.map(cat => (
                  <button key={cat} type="button" onClick={() => setCatFilter(cat)} style={{
                    padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--font)', fontSize: 13, fontWeight: catFilter === cat ? 600 : 400,
                    background: catFilter === cat ? 'var(--accent)' : 'var(--bg3)',
                    color: catFilter === cat ? '#0f1117' : 'var(--text2)',
                    transition: 'all 0.15s',
                  }}>{cat}</button>
                ))}
              </div>

              {/* ── candidate info bar ── */}
              <div className="card" style={{ marginBottom: 20, display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 14 }}>
                <div>
                  <span className="label">Candidate name</span>
                  <strong>{candidateName || '—'}</strong>
                </div>
                <div>
                  <span className="label">Test date</span>
                  <strong>{testDate}</strong>
                </div>
                <div>
                  <span className="label">Role</span>
                  <strong style={{ textTransform: 'capitalize' }}>{roleLabel}</strong>
                </div>
                <div>
                  <span className="label">Status</span>
                  <strong style={{ textTransform: 'capitalize', color: testData.status === 'marked' ? 'var(--green)' : 'var(--amber)' }}>
                    {testData.status}
                  </strong>
                </div>
              </div>

              {/* ── question cards ── */}
              {visibleQuestions.map((q) => {
                const answer = answers.find(a => a.question_index === q.index);
                const mark   = answer ? (marks[answer.id] || { is_correct: null, mark_reason: '' }) : null;

                return (
                  <div key={q.index} className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
                    {/* question header */}
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg4)', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700, color: 'var(--text2)', flexShrink: 0, marginTop: 1 }}>
                        {q.index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: '0 0 8px', fontWeight: 500, fontSize: 15, lineHeight: 1.6 }}>
                          {q.question}
                        </p>
                        {q.category && <CategoryBadge label={q.category} size="sm" />}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                      {/* ── model answer (admin only) ── */}
                      <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border)', background: 'var(--bg3)' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent)', marginBottom: 10 }}>
                          Model answer
                        </div>
                        <ModelAnswer ma={q.model_answer} />
                      </div>

                      {/* ── candidate's answer + marking ── */}
                      <div style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text2)', marginBottom: 10 }}>
                          Candidate's answer
                        </div>

                        {answer ? (
                          <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: answer.answer_text ? 'var(--text)' : 'var(--text3)', marginBottom: 16, minHeight: 60 }}>
                            {answer.answer_text || <em>No answer provided.</em>}
                          </div>
                        ) : (
                          <div style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 16, fontStyle: 'italic' }}>
                            No answer recorded.
                          </div>
                        )}

                        {/* mark buttons */}
                        {isAdmin && answer && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text2)', marginBottom: 8 }}>
                              Mark candidate's answer
                            </div>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                              <button type="button" onClick={() => setMarks(p => ({ ...p, [answer.id]: { ...mark, is_correct: true } }))}
                                className={`btn btn-sm ${mark?.is_correct === true ? 'btn-success' : 'btn-ghost'}`}>
                                ✓ Correct
                              </button>
                              <button type="button" onClick={() => setMarks(p => ({ ...p, [answer.id]: { ...mark, is_correct: false } }))}
                                className={`btn btn-sm ${mark?.is_correct === false ? 'btn-danger' : 'btn-ghost'}`}>
                                ✗ Incorrect
                              </button>
                            </div>
                            <input
                              value={mark?.mark_reason || ''}
                              onChange={e => setMarks(p => ({ ...p, [answer.id]: { ...mark, mark_reason: e.target.value } }))}
                              placeholder="Reason (optional)…"
                              style={{ fontSize: 13 }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* ── save marks button ── */}
              {isAdmin && answers.length > 0 && (
                <div style={{ margin: '8px 0 24px' }}>
                  {markMsg && <p style={{ marginBottom: 10, fontSize: 13, color: markMsg === 'Marks saved.' ? 'var(--green)' : 'var(--red)' }}>{markMsg}</p>}
                  <button type="button" className="btn btn-primary" onClick={handleMarkSave} disabled={marking}>
                    {marking ? 'Saving…' : 'Save All Marks'}
                  </button>
                </div>
              )}

              {/* ── scoring summary ── */}
              <div className="card" style={{ borderTop: '2px solid var(--border2)' }}>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
                  {/* correct */}
                  <div style={{ textAlign: 'center', padding: '10px 20px', background: 'var(--green-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(76,175,133,0.2)', minWidth: 80 }}>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Correct</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--green)', fontFamily: 'var(--font-display)' }}>{correctCount}</div>
                  </div>
                  {/* incorrect */}
                  <div style={{ textAlign: 'center', padding: '10px 20px', background: 'var(--red-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(224,92,92,0.2)', minWidth: 80 }}>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Incorrect</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--red)', fontFamily: 'var(--font-display)' }}>{incorrectCount}</div>
                  </div>
                  {/* unmarked */}
                  <div style={{ textAlign: 'center', padding: '10px 20px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border2)', minWidth: 80 }}>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unmarked</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text2)', fontFamily: 'var(--font-display)' }}>{unmarkedCount}</div>
                  </div>

                  <div style={{ borderLeft: '1px solid var(--border)', height: 60 }} />

                  {/* score */}
                  <div style={{ textAlign: 'center', padding: '10px 24px', background: score >= 7 ? 'var(--green-bg)' : score >= 4 ? 'var(--amber-bg)' : 'var(--red-bg)', borderRadius: 'var(--radius-sm)', border: `1px solid ${score >= 7 ? 'rgba(76,175,133,0.2)' : score >= 4 ? 'rgba(232,169,74,0.2)' : 'rgba(224,92,92,0.2)'}`, minWidth: 100 }}>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Score (out of 10)</div>
                    <div style={{ fontSize: 32, fontWeight: 700, color: score >= 7 ? 'var(--green)' : score >= 4 ? 'var(--amber)' : 'var(--red)', fontFamily: 'var(--font-display)' }}>{score}</div>
                  </div>
                </div>

                {/* final score callout */}
                <div style={{ padding: '14px 18px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>Final score to enter in scorecard</div>
                    <div style={{ fontSize: 13, color: 'var(--text2)' }}>Once all questions are marked, enter the score above into the <em>Test results</em> field on the main scorecard.</div>
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-display)', whiteSpace: 'nowrap' }}>
                    {score} <span style={{ fontSize: 16, color: 'var(--text2)', fontWeight: 400 }}>/ 10</span>
                  </div>
                </div>

                {remaining > 0 && (
                  <p style={{ marginTop: 12, fontSize: 13, color: 'var(--amber)' }}>
                    {remaining} question{remaining > 1 ? 's' : ''} remaining — mark all questions to finalise the score.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'scorecard' && (
        <ScorecardForm applicantId={Number(id)} authToken={tok()} isAdmin={isAdmin} />
      )}
    </div>
  );
}
